import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { SectionTitle } from "@/components/common";
import { toast } from "sonner";
import { Plus, Trash2, Save, Building2, Network, Users } from "lucide-react";

function ChipGroup({ label, options, selected, onToggle, testid }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-muted-foreground">{label}</Label>
      <div className="flex flex-wrap gap-2 mt-2" data-testid={testid}>
        {options.map((o) => {
          const on = selected.includes(o);
          return (
            <button key={o} type="button" onClick={() => onToggle(o)} data-testid={`${testid}-${o}`}
              className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-all
                ${on ? "bg-[#0f2557] text-white border-[#0f2557] dark:bg-[#29abe2] dark:border-[#29abe2]"
                     : "bg-card text-muted-foreground border-border hover:border-[#29abe2]/50"}`}>
              {o}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function ProjectSetup() {
  const navigate = useNavigate();
  const { pid } = useParams();
  const editing = !!pid;

  const { data: catalog } = useQuery({ queryKey: ["catalog"], queryFn: async () => (await api.get("/catalog")).data });
  const { data: existing } = useQuery({
    queryKey: ["project", pid], enabled: editing,
    queryFn: async () => (await api.get(`/projects/${pid}`)).data,
  });

  const [form, setForm] = useState({
    client_name: "", name: "", industry: "Manufacturing",
    systems: { sap_product: "", deployment: "", btp: false, middleware: "None", version: "",
      sap_modules: [], crm_systems: [], other_systems: [] },
    team: [], stakeholders: [],
  });

  useEffect(() => { if (existing) setForm(existing); }, [existing]);

  const setSys = (k, v) => setForm((f) => ({ ...f, systems: { ...f.systems, [k]: v } }));
  const toggle = (k, o) => setSys(k, form.systems[k].includes(o) ? form.systems[k].filter((x) => x !== o) : [...form.systems[k], o]);

  const addPerson = (key) => setForm((f) => ({ ...f, [key]: [...f[key], { name: "", org: "", role: "", email: "" }] }));
  const updPerson = (key, i, field, v) => setForm((f) => {
    const arr = [...f[key]]; arr[i] = { ...arr[i], [field]: v }; return { ...f, [key]: arr };
  });
  const rmPerson = (key, i) => setForm((f) => ({ ...f, [key]: f[key].filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!form.client_name || !form.name) { toast.error("Client name and project name are required"); return; }
    try {
      if (editing) { await api.put(`/projects/${pid}`, form); toast.success("Project updated"); navigate(`/projects/${pid}`); }
      else { const res = await api.post("/projects", form); toast.success("Project created"); navigate(`/projects/${res.data.id}`); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save project"); }
  };

  if (!catalog) return null;

  return (
    <div className="space-y-8 animate-fade-up max-w-4xl">
      <SectionTitle eyebrow={editing ? "Edit" : "New Engagement"} title={editing ? "Edit Project" : "Project & Landscape Setup"} />

      <section className="rounded-xl border bg-card p-6 space-y-4">
        <div className="flex items-center gap-2 text-[#0f2557] dark:text-[#29abe2] font-semibold"><Building2 size={18} /> Client & Project</div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>Client name *</Label><Input data-testid="client-name" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} placeholder="Acme Manufacturing Ltd." /></div>
          <div><Label>Project name *</Label><Input data-testid="project-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Order-to-Cash Transformation" /></div>
          <div>
            <Label>Industry</Label>
            <Select value={form.industry} onValueChange={(v) => setForm({ ...form, industry: v })}>
              <SelectTrigger data-testid="industry-select"><SelectValue /></SelectTrigger>
              <SelectContent>{catalog.industries.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
      </section>

      <section className="rounded-xl border bg-card p-6 space-y-5">
        <div className="flex items-center gap-2 text-[#0f2557] dark:text-[#29abe2] font-semibold"><Network size={18} /> SAP Landscape</div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>SAP product</Label>
            <Select value={form.systems.sap_product} onValueChange={(v) => setSys("sap_product", v)}>
              <SelectTrigger data-testid="sap-product"><SelectValue placeholder="Select product" /></SelectTrigger>
              <SelectContent>{catalog.sap_products.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Middleware</Label>
            <Select value={form.systems.middleware} onValueChange={(v) => setSys("middleware", v)}>
              <SelectTrigger data-testid="middleware"><SelectValue /></SelectTrigger>
              <SelectContent>{catalog.middleware.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Version / Release</Label><Input data-testid="sap-version" value={form.systems.version} onChange={(e) => setSys("version", e.target.value)} placeholder="e.g. 2023 FPS02" /></div>
          <div><Label>Deployment</Label><Input data-testid="deployment" value={form.systems.deployment} onChange={(e) => setSys("deployment", e.target.value)} placeholder="Cloud / On-Premise / Hybrid" /></div>
        </div>
        <ChipGroup label="SAP Modules" options={catalog.sap_modules} selected={form.systems.sap_modules} onToggle={(o) => toggle("sap_modules", o)} testid="chip-modules" />
        <ChipGroup label="CRM Systems" options={catalog.crm_systems} selected={form.systems.crm_systems} onToggle={(o) => toggle("crm_systems", o)} testid="chip-crm" />
        <ChipGroup label="Other Connected Systems" options={catalog.other_systems} selected={form.systems.other_systems} onToggle={(o) => toggle("other_systems", o)} testid="chip-other" />
      </section>

      {["team", "stakeholders"].map((key) => (
        <section key={key} className="rounded-xl border bg-card p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-[#0f2557] dark:text-[#29abe2] font-semibold capitalize"><Users size={18} /> {key === "team" ? "Delivery Team" : "Client Stakeholders"}</div>
            <Button variant="outline" size="sm" onClick={() => addPerson(key)} data-testid={`add-${key}`} className="gap-1"><Plus size={14} /> Add</Button>
          </div>
          {form[key].length === 0 && <p className="text-sm text-muted-foreground">No {key} added yet.</p>}
          <div className="space-y-2">
            {form[key].map((p, i) => (
              <div key={i} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_1fr_1fr_auto] gap-2 items-center">
                <Input placeholder="Name" value={p.name} onChange={(e) => updPerson(key, i, "name", e.target.value)} data-testid={`${key}-name-${i}`} />
                <Input placeholder="Organization" value={p.org} onChange={(e) => updPerson(key, i, "org", e.target.value)} />
                <Input placeholder="Role" value={p.role} onChange={(e) => updPerson(key, i, "role", e.target.value)} />
                <Input placeholder="Email" value={p.email} onChange={(e) => updPerson(key, i, "email", e.target.value)} />
                <Button variant="ghost" size="icon" onClick={() => rmPerson(key, i)} className="text-red-500"><Trash2 size={15} /></Button>
              </div>
            ))}
          </div>
        </section>
      ))}

      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={() => navigate(-1)}>Cancel</Button>
        <Button onClick={save} data-testid="save-project" className="gap-2"><Save size={16} /> {editing ? "Save changes" : "Create project"}</Button>
      </div>
    </div>
  );
}
